import { useCallback, useEffect, useMemo } from 'react'
import { Wallet, Clock, Zap, Sparkles, Check, Images, FileText, Hash } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useCreateModel } from './context'
import {
  IMAGE_MODEL_FAMILIES,
  MODEL_TYPE_DESCRIPTIONS,
  getModelById,
  getDefaultTrainerForType,
  getImageModels,
  MIN_IMAGES,
} from './constants'
import type { TrainingType, AdvancedFieldDef } from './types'

// ─── Dataset Quality Score ──────────────────────────────────────

function QualityScore() {
  const { state } = useCreateModel()

  const { score, label, color, tips } = useMemo(() => {
    let s = 0
    const t: string[] = []

    // Image count (0-30)
    if (state.images.length >= 20) s += 30
    else if (state.images.length >= 10) s += 25
    else if (state.images.length >= MIN_IMAGES) s += 15
    else { s += (state.images.length / MIN_IMAGES) * 10; t.push(`Add ${MIN_IMAGES - state.images.length} more images`) }

    // Resolution (0-20)
    const goodRes = state.images.filter((img) => Math.min(img.width, img.height) >= 512).length
    const resRatio = state.images.length > 0 ? goodRes / state.images.length : 0
    s += resRatio * 20
    if (resRatio < 1 && state.images.length > 0) t.push('Some images are low resolution')

    // Captions (0-25)
    const captioned = state.images.filter((img) => img.captionStatus !== 'none').length
    const capRatio = state.images.length > 0 ? captioned / state.images.length : 0
    s += capRatio * 25
    if (capRatio < 1 && state.images.length > 0) t.push('Caption all images for better results')

    // Trigger word (0-10)
    if (state.triggerWord.trim()) s += 10
    else t.push('Set a trigger word')

    // No issues (0-15)
    const withIssues = state.images.filter((img) => img.issues.length > 0).length
    const issueRatio = state.images.length > 0 ? 1 - (withIssues / state.images.length) : 1
    s += issueRatio * 15
    if (withIssues > 0) t.push(`Fix ${withIssues} image issue${withIssues > 1 ? 's' : ''}`)

    const score = Math.round(s)
    const label = score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : score >= 40 ? 'Fair' : 'Getting started'
    const color = score >= 80 ? 'text-green-500' : score >= 60 ? 'text-blue-500' : score >= 40 ? 'text-amber-500' : 'text-muted-foreground'
    return { score, label, color, tips: t.slice(0, 2) }
  }, [state.images, state.triggerWord])

  const radius = 28
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference
  const strokeColor = score >= 80 ? '#22c55e' : score >= 60 ? '#3b82f6' : score >= 40 ? '#f59e0b' : '#64748b'

  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-background/50 p-3">
      <div className="relative shrink-0">
        <svg width="64" height="64" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r={radius} fill="none" stroke="currentColor" strokeWidth="3" className="text-muted/30" />
          <circle cx="32" cy="32" r={radius} fill="none" stroke={strokeColor} strokeWidth="3" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={offset} transform="rotate(-90 32 32)" className="transition-all duration-700" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={cn('text-sm font-bold', color)}>{score}</span>
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className={cn('text-xs font-semibold', color)}>{label}</p>
        {tips.map((tip, i) => (
          <p key={i} className="text-[11px] text-muted-foreground">{tip}</p>
        ))}
        {tips.length === 0 && (
          <p className="flex items-center gap-1 text-[11px] text-green-500">
            <Check className="h-3 w-3" /> Dataset ready
          </p>
        )}
      </div>
    </div>
  )
}

// ─── Training Presets ───────────────────────────────────────────

const PRESETS = [
  { key: 'quick', label: 'Quick', steps: 250, description: 'Fast test run' },
  { key: 'standard', label: 'Standard', steps: 1000, description: 'Recommended' },
  { key: 'quality', label: 'Quality', steps: 3000, description: 'Best results' },
] as const

function TrainingPresets() {
  const { state, dispatch, selectedTrainer } = useCreateModel()
  if (!selectedTrainer) return null

  const activePreset = PRESETS.find((p) => p.steps === state.steps)?.key ?? null

  return (
    <div className="flex flex-col gap-2">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Quality</label>
      <div className="grid grid-cols-3 gap-1.5">
        {PRESETS.map((preset) => {
          const isActive = activePreset === preset.key
          const cost = preset.steps * selectedTrainer.pricePerUnit * 2
          return (
            <button
              key={preset.key}
              onClick={() => {
                const clamped = Math.min(Math.max(preset.steps, selectedTrainer.minSteps), selectedTrainer.maxSteps)
                dispatch({ type: 'SET_STEPS', payload: clamped })
              }}
              className={cn(
                'flex flex-col items-center gap-0.5 rounded-lg border px-2 py-2 text-center transition-all',
                isActive ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-primary/50',
              )}
            >
              <span className="text-xs font-medium">{preset.label}</span>
              <span className="text-[10px] text-muted-foreground">{preset.steps} steps</span>
              <span className="text-[10px] font-medium">${cost.toFixed(0)}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ─── Advanced Field ─────────────────────────────────────────────

function AdvancedField({ field }: { field: AdvancedFieldDef }) {
  const { state, dispatch } = useCreateModel()
  const value = state.advancedSettings[field.key] ?? field.defaultValue
  const setValue = (v: unknown) => dispatch({ type: 'SET_ADVANCED', payload: { key: field.key, value: v } })

  switch (field.type) {
    case 'toggle':
      return (
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium">{field.label}</p>
            <p className="text-[11px] text-muted-foreground">{field.description}</p>
          </div>
          <Switch size="sm" checked={value as boolean} onCheckedChange={setValue} />
        </div>
      )
    case 'select':
      return (
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium">{field.label}</p>
          <Select value={String(value)} onValueChange={setValue}>
            <SelectTrigger size="sm"><SelectValue /></SelectTrigger>
            <SelectContent className="z-[70]">
              {field.options?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )
    case 'number':
      return (
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium">{field.label}</p>
          <Input type="number" value={String(value)} onChange={(e) => setValue(Number(e.target.value))}
            min={field.min} max={field.max} step={field.step} className="h-7 w-20 text-xs" />
        </div>
      )
    case 'text':
      return (
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium">{field.label}</p>
          <Input type="text" value={String(value ?? '')} onChange={(e) => setValue(e.target.value)}
            placeholder={field.description} className="h-7 text-xs" />
        </div>
      )
    default:
      return null
  }
}

// ─── Image-Only Model Selector ──────────────────────────────────

function ImageModelSelector() {
  const { state, dispatch } = useCreateModel()

  const selectModel = useCallback(
    (trainerId: string) => {
      const model = getModelById(trainerId)
      if (!model) return
      const advancedDefaults: Record<string, unknown> = {}
      for (const field of model.advancedFields) advancedDefaults[field.key] = field.defaultValue
      dispatch({
        type: 'SELECT_TRAINER',
        payload: { trainerId, defaults: { steps: model.defaultSteps, learningRate: model.defaultLearningRate, advanced: advancedDefaults } },
      })
    },
    [dispatch],
  )

  // Auto-select on mount
  useEffect(() => {
    if (!state.selectedTrainerId) selectModel(getDefaultTrainerForType(state.modelType))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const imageModels = getImageModels()
  const recommendedId = getDefaultTrainerForType(state.modelType)

  const formatPrice = (model: (typeof imageModels)[0]) =>
    `$${model.pricePerUnit}/${model.pricingUnit}`

  return (
    <Select value={state.selectedTrainerId ?? ''} onValueChange={selectModel}>
      <SelectTrigger size="sm">
        <SelectValue placeholder="Select model..." />
      </SelectTrigger>
      <SelectContent className="z-[70] max-h-[300px]">
        {IMAGE_MODEL_FAMILIES.map((fg) => {
          const models = imageModels.filter((m) => m.family === fg.family)
          if (models.length === 0) return null
          return (
            <SelectGroup key={fg.family}>
              <SelectLabel>{fg.label}</SelectLabel>
              {models.map((model) => (
                <SelectItem key={model.id} value={model.id}>
                  <span className="flex items-center gap-1.5">
                    <span className="truncate">{model.name}</span>
                    <span className="shrink-0 text-[10px] text-muted-foreground">{formatPrice(model)}</span>
                    {model.id === recommendedId && <Badge variant="primary" size="sm" className="h-4 text-[10px]">Best</Badge>}
                    {model.tags.includes('popular') && model.id !== recommendedId && <Badge variant="secondary" size="sm" className="h-4 text-[10px]">Top</Badge>}
                    {model.tags.includes('budget') && <Badge variant="outline" size="sm" className="h-4 text-[10px]">$</Badge>}
                  </span>
                </SelectItem>
              ))}
            </SelectGroup>
          )
        })}
      </SelectContent>
    </Select>
  )
}

// ─── Review Summary ─────────────────────────────────────────────

function ReviewSummary() {
  const { state, selectedTrainer, estimatedCost } = useCreateModel()
  if (!selectedTrainer || state.images.length < MIN_IMAGES) return null

  const captionedCount = state.images.filter((i) => i.captionStatus !== 'none').length
  const totalSizeMB = state.images.reduce((sum, i) => sum + i.sizeBytes, 0) / 1024 / 1024

  return (
    <div className="rounded-lg border border-border bg-background/50 p-3">
      <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Review</p>
      <div className="flex flex-col gap-1.5 text-xs">
        <div className="flex items-center gap-2">
          <Images className="h-3 w-3 text-muted-foreground" />
          <span>{state.images.length} images &middot; {totalSizeMB.toFixed(0)}MB &middot; {captionedCount} captioned</span>
        </div>
        {state.triggerWord && (
          <div className="flex items-center gap-2">
            <Hash className="h-3 w-3 text-muted-foreground" />
            <span className="font-mono text-[11px]">{state.triggerWord}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <FileText className="h-3 w-3 text-muted-foreground" />
          <span>{selectedTrainer.name} &middot; {state.steps} steps</span>
        </div>
        {estimatedCost && (
          <div className="flex items-center gap-2">
            <Zap className="h-3 w-3 text-muted-foreground" />
            <span className="font-medium">${estimatedCost.totalCost.toFixed(2)} &middot; {estimatedCost.estimatedDuration}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Config Panel (Right Side) ──────────────────────────────────

export function ConfigPanel() {
  const { state, dispatch, selectedTrainer, estimatedCost } = useCreateModel()

  const selectModel = useCallback(
    (trainerId: string) => {
      const model = getModelById(trainerId)
      if (!model) return
      const advancedDefaults: Record<string, unknown> = {}
      for (const field of model.advancedFields) advancedDefaults[field.key] = field.defaultValue
      dispatch({
        type: 'SELECT_TRAINER',
        payload: { trainerId, defaults: { steps: model.defaultSteps, learningRate: model.defaultLearningRate, advanced: advancedDefaults } },
      })
    },
    [dispatch],
  )

  const handleTypeChange = useCallback(
    (v: string) => {
      dispatch({ type: 'SET_MODEL_TYPE', payload: v as TrainingType })
      selectModel(getDefaultTrainerForType(v as TrainingType))
    },
    [dispatch, selectModel],
  )

  return (
    <div className="flex flex-col gap-4">
      {/* Quality Score */}
      <QualityScore />

      <Separator />

      {/* ── MODEL FIRST ── */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Model</label>
          {selectedTrainer && (
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Sparkles className="h-2.5 w-2.5" /> Recommended
            </span>
          )}
        </div>
        <ImageModelSelector />
        {selectedTrainer && (
          <p className="text-[11px] text-muted-foreground">{selectedTrainer.description}</p>
        )}
      </div>

      <Separator />

      {/* Type */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</label>
        <Tabs value={state.modelType} onValueChange={handleTypeChange}>
          <TabsList className="h-8 w-full">
            <TabsTrigger value="style" className="flex-1 text-xs">Style</TabsTrigger>
            <TabsTrigger value="object" className="flex-1 text-xs">Object</TabsTrigger>
            <TabsTrigger value="character" className="flex-1 text-xs">Character</TabsTrigger>
          </TabsList>
        </Tabs>
        <p className="text-[11px] text-muted-foreground">{MODEL_TYPE_DESCRIPTIONS[state.modelType]}</p>
      </div>

      <Separator />

      {/* Trigger Word */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Trigger word</label>
        <Input
          placeholder="e.g. ohwx, sks"
          value={state.triggerWord}
          onChange={(e) => dispatch({ type: 'SET_TRIGGER_WORD', payload: e.target.value })}
          className="h-8 text-sm"
        />
      </div>

      {/* Presets + Steps */}
      {selectedTrainer && (
        <>
          <Separator />
          <TrainingPresets />

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Steps</label>
              <Input type="number" value={state.steps}
                onChange={(e) => dispatch({ type: 'SET_STEPS', payload: Number(e.target.value) })}
                min={selectedTrainer.minSteps} max={selectedTrainer.maxSteps}
                className="h-7 w-20 text-right text-xs" />
            </div>
            <Slider size="sm" value={[state.steps]}
              onValueChange={([v]) => { if (v !== undefined) dispatch({ type: 'SET_STEPS', payload: v }) }}
              min={selectedTrainer.minSteps} max={selectedTrainer.maxSteps} step={100} />
          </div>

          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">LR</label>
            <Input type="number" value={state.learningRate}
              onChange={(e) => dispatch({ type: 'SET_LEARNING_RATE', payload: Number(e.target.value) })}
              step={0.00001} min={0.000001} max={0.01}
              className="h-7 w-24 text-right text-xs" />
          </div>
        </>
      )}

      {/* Advanced */}
      {selectedTrainer && selectedTrainer.advancedFields.length > 0 && (
        <>
          <Separator />
          <details className="group">
            <summary className="flex cursor-pointer items-center justify-between text-xs font-medium uppercase tracking-wider text-muted-foreground list-none">
              Advanced
              <span className="text-[10px] group-open:hidden">Show</span>
              <span className="hidden text-[10px] group-open:inline">Hide</span>
            </summary>
            <div className="mt-3 flex flex-col gap-3">
              {selectedTrainer.advancedFields.map((field) => (
                <AdvancedField key={field.key} field={field} />
              ))}
            </div>
          </details>
        </>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Review Summary */}
      <ReviewSummary />

      {/* Cost */}
      {estimatedCost && selectedTrainer && (
        <div className="rounded-lg border border-border bg-background/50 p-3">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Zap className="h-3 w-3" />Cost</span>
              <span className="text-base font-bold">${estimatedCost.totalCost.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="h-3 w-3" />Time</span>
              <span className="text-xs">{estimatedCost.estimatedDuration}</span>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Wallet className="h-3 w-3" />Balance</span>
              <span className={cn('text-xs font-medium', !estimatedCost.hasSufficientFunds ? 'text-destructive' : 'text-foreground')}>
                ${estimatedCost.walletBalance.toFixed(2)} → ${estimatedCost.remainingBalance.toFixed(2)}
              </span>
            </div>
            {!estimatedCost.hasSufficientFunds && (
              <button className="text-left text-[11px] font-medium text-primary hover:underline">Add funds →</button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
